import { useEffect, useState } from "react"
import { fetchClaimOffer, acceptClaimOffer, dismissClaimOffer } from "../../lib/api"

function shortLinkLabel(url) {
  return (url || "").replace(/^https?:\/\//, "")
}

function madeOn(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
}

// Codes made with this account's address on another device, never confirmed
// from their email (#329, docs/specs/2026-10-19-signin-consent-claim.md). The
// server lists them only after a sign-in that proved the address, and nothing
// is added until the owner presses Add. Someone may have typed this address on
// codes that are not theirs, so "Not mine" is always one press away, and it
// takes a code off this card for good.
//
// Fetches its own list and renders nothing when there is none. After every
// press it reads the list again, so a code that stopped qualifying meanwhile
// drops out, and codes past the first screen come up next.
export default function FoundCodesCard({ onAdded, onDismissed }) {
  const [codes, setCodes] = useState([])
  const [busy, setBusy] = useState(null) // "add" | "dismiss" | a token
  const [error, setError] = useState(null)

  function load() {
    return fetchClaimOffer()
      .then((data) => setCodes(data?.codes || []))
      .catch(() => {}) // the card is optional; the dashboard works without it
  }

  useEffect(() => { load() }, [])

  if (codes.length === 0) return null

  const one = codes.length === 1
  const tokens = codes.map((c) => c.token)

  async function run(kind, action) {
    setBusy(kind)
    setError(null)
    try {
      await action()
      await load()
    } catch (e) {
      setError(e.message || "That didn't work. Try again.")
    } finally {
      setBusy(null)
    }
  }

  const addAll = () => run("add", async () => {
    const { added } = await acceptClaimOffer(tokens)
    onAdded?.(added || 0)
  })

  const dismissAll = () => run("dismiss", async () => {
    await dismissClaimOffer(tokens)
    onDismissed?.(one ? "Done. We won't show that code again." : "Done. We won't show those codes again.")
  })

  const dismissOne = (token) => run(token, async () => {
    await dismissClaimOffer([ token ])
    setCodes((list) => list.filter((c) => c.token !== token))
  })

  const focusRing = "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 dark:focus-visible:ring-offset-coal"
  const ghost = "border border-[color:var(--color-border)] dark:border-storm rounded-lg text-ink dark:text-cloud hover:border-voltage transition disabled:opacity-50 " + focusRing

  return (
    <section
      aria-labelledby="found-codes-heading"
      className="border border-voltage/40 bg-[color:var(--color-cloud)] dark:bg-coal rounded-lg mb-3"
    >
      <header className="flex flex-col gap-3 min-[640px]:flex-row min-[640px]:items-start min-[640px]:justify-between px-4 py-3 border-b border-[color:var(--color-border)] dark:border-storm">
        <div className="min-w-0">
          <h2 id="found-codes-heading" className="font-display font-semibold text-ink dark:text-cloud text-base">
            We found {one ? "1 code" : `${codes.length} codes`} made with this address on another device.
          </h2>
          <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1 leading-relaxed">
            {one
              ? "Add it to your account to change where it points and see its scans. Didn't make it? Choose Not mine and we won't show it again."
              : "Add them to your account to change where they point and see their scans. If one isn't yours, choose Not mine on it first."}
          </p>
          {error && <p role="alert" className="text-sm text-fail mt-2">{error}</p>}
        </div>
        <div className="flex flex-wrap gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={addAll}
            disabled={busy !== null}
            className={"bg-voltage text-coal rounded-lg px-3 py-2 text-sm font-medium hover:bg-voltage-dark transition disabled:opacity-50 " + focusRing}
          >
            {busy === "add" ? "Adding…" : one ? "Add it to my account" : "Add them to my account"}
          </button>
          <button
            type="button"
            onClick={dismissAll}
            disabled={busy !== null}
            className={"px-3 py-2 text-sm font-medium " + ghost}
          >
            {busy === "dismiss" ? "Hiding…" : one ? "Not mine" : "None are mine"}
          </button>
        </div>
      </header>

      <ul className="divide-y divide-[color:var(--color-border)] dark:divide-storm">
        {codes.map((code) => (
          <li key={code.token} className="px-4 py-3 flex gap-3 items-center">
            <img
              src={code.image_url}
              alt=""
              aria-hidden="true"
              className="w-10 h-10 border border-[color:var(--color-border)] dark:border-storm rounded bg-white flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink dark:text-cloud truncate" title={code.display_label}>{code.display_label}</div>
              <div className="font-mono text-xs text-voltage-dark dark:text-voltage-light truncate">{shortLinkLabel(code.short_url)}</div>
              <div className="text-xs text-[color:var(--color-muted)] dark:text-muted-light truncate">
                → <span className="font-mono">{code.destination_url}</span>
              </div>
              <div className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">
                Made <span className="font-mono">{madeOn(code.created_at)}</span>
              </div>
            </div>
            {!one && (
              <button
                type="button"
                onClick={() => dismissOne(code.token)}
                disabled={busy !== null}
                aria-label={`Not mine: ${shortLinkLabel(code.short_url)}`}
                className={"px-3 py-1.5 text-xs font-medium flex-shrink-0 " + ghost}
              >
                {busy === code.token ? "Hiding…" : "Not mine"}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
